// src/components/auth/LoginScreen.js
import { AlertCircle, Eye, EyeOff, Lock, Mail, ShieldCheck, Wifi, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import logo from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';
import { getErrorMessage } from '../../utils/errorUtils';
import { validateForm, validators } from '../../utils/validation';

const LoginScreen = ({ onShowRegister }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showPassword, setShowPassword] = useState(false);

  const validationRules = {
    email: [validators.required, validators.email],
    password: [validators.required, validators.minLength(6)]
  };

  // Check for remembered login preference
  useEffect(() => {
    const remembered = localStorage.getItem('rememberLogin');
    if (remembered === 'true') {
      setRememberMe(true);
    }
  }, []);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general error when user modifies any field
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLocked) {
      return;
    }

    // Check if online
    if (!isOnline) {
      setErrors({
        general: 'No internet connection. Please check your network and try again.'
      });
      return;
    }

    // Validate form
    const { isValid, errors: validationErrors } = validateForm(formData, validationRules);

    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await login(formData);
      setLoginAttempts(0);

      // Handle remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberLogin', 'true');
      } else {
        localStorage.removeItem('rememberLogin');
      }

      // Success! The AuthContext will handle the redirect
    } catch (error) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      // Handle different types of errors
      const errorMessage = getErrorMessage(error);

      if (newAttempts >= 3) {
        setIsLocked(true);
        setErrors({
          general: 'Too many failed attempts. Account locked for 5 minutes.'
        });

        // Auto unlock after 5 minutes (for demo purposes, using 30 seconds)
        setTimeout(() => {
          setIsLocked(false);
          setLoginAttempts(0);
          setErrors({});
        }, 30000);
      } else {
        // Check if it's a network error
        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          setErrors({
            general: errorMessage
          });
        } else {
          setErrors({
            general: `${errorMessage} ${3 - newAttempts} attempts remaining.`
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // This would typically navigate to a forgot password page
    alert('Forgot password functionality would redirect to password reset page');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      {/* Soft brand-tinted backdrop */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-accent-100 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Connection Status Indicator */}
        {!isOnline && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-2">
            <WifiOff className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-600">
              No internet connection
            </span>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="p-8 text-center border-b border-gray-100">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-md border border-gray-100 mb-5">
              <img src={logo} alt="InvisiGuard" className="w-14 h-14 object-contain" />
            </div>

            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              InvisiGuard
            </h1>
            <p className="inline-flex items-center text-sm font-medium text-accent-700 bg-accent-50 border border-accent-100 rounded-full px-3 py-1 mb-3">
              Admin Portal
            </p>
            <p className="text-gray-500 text-sm mb-5">
              Invisible. Authentic. Trusted.
            </p>

            {/* Connection Status Badge */}
            <div className="flex items-center justify-center">
              {isOnline ? (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-accent-50 border border-accent-200 rounded-full">
                  <Wifi className="h-3 w-3 text-accent-600" />
                  <span className="text-xs text-accent-700 font-medium">Secure Connection</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full">
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600 font-medium">Offline Mode</span>
                </div>
              )}
            </div>
          </div>

          {/* Login Form */}
          <div className="p-8">
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* General Error */}
              {errors.general && (
                <div className="p-4 rounded-xl border bg-red-50 border-red-200 text-red-700 flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <p className="text-sm font-medium">{errors.general}</p>
                </div>
              )}

              {/* Email Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Mail className="inline h-4 w-4 mr-2 -mt-0.5 text-gray-400" />
                  Email Address
                </label>
                <input
                  name="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading || isLocked || !isOnline}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-100 transition-colors disabled:opacity-50"
                  autoComplete="email"
                  required
                />
                {errors.email && (
                  <p className="text-sm text-red-500 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Lock className="inline h-4 w-4 mr-2 -mt-0.5 text-gray-400" />
                  Password
                </label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading || isLocked || !isOnline}
                    className="w-full px-4 py-3 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-100 transition-colors disabled:opacity-50"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center space-x-2 text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading || isLocked || !isOnline}
                    className="w-4 h-4 text-primary-600 bg-gray-50 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading || isLocked || !isOnline}
                  className="text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-50 hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isLocked || !isOnline}
                className="w-full py-3.5 px-6 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Authenticating...</span>
                  </>
                ) : isLocked ? (
                  <>
                    <AlertCircle className="h-5 w-5" />
                    <span>Account Locked</span>
                  </>
                ) : !isOnline ? (
                  <>
                    <WifiOff className="h-5 w-5" />
                    <span>Offline</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5" />
                    <span>Sign In Securely</span>
                  </>
                )}
              </button>
            </form>

            {/* Register Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={onShowRegister}
                  className="text-primary-600 hover:text-primary-700 font-semibold transition-colors hover:underline"
                >
                  Create Account
                </button>
              </p>
            </div>
          </div>

          {/* Footer */}
          {(loginAttempts > 0 && loginAttempts < 3) || isLocked ? (
            <div className="p-5 bg-gray-50 border-t border-gray-100 text-center">
              {loginAttempts > 0 && loginAttempts < 3 && (
                <p className="text-xs text-orange-500">
                  ⚠️ {loginAttempts} failed attempt{loginAttempts > 1 ? 's' : ''} detected
                </p>
              )}
              {isLocked && (
                <p className="text-xs text-red-500">
                  🔒 Security lockdown active - Unlocking in 30 seconds
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
